/* eslint-disable import/extensions, import/named, import/no-unresolved, import/namespace, import/no-extraneous-dependencies, import/no-duplicates */
import "core-js";

// we have to load this module first to have our updated config
import "./app-config";
import { configureStore } from "@payping/redux";
import { initI18next, localeNameSpaces } from "@payping/utils/localeUtils";
import * as Sentry from "@sentry/react";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import type { Persistor } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import ResizeObserver from "resize-observer-polyfill";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";

import { apiConfigs } from "@payping/api";
import userBrowserFingerPrint from "@payping/utils/userBrowserFingerPrint";
import { commonConstants } from "@payping/utils/platformConstants";

import { browserCookie } from "@payping/utils/cookie";
import { AppErrorBoundary } from "./AppErrorBoundary";
import { localeResources } from "./assets/locales/resources";
import { AppStoreStates, reduxConfig } from "./redux/configs";
import reportWebVitals from "./reportWebVitals";
import { sendToAnalytics } from "./web-vitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { setToken } from "./utils";
import App from "./App";
import "./apiConfigs";
import "./index.scss";
import Providers from "./refund/providers";
import { bootstrapJotai } from "@payping/utilities-browser/state-management";
import { Store as JotaiStore, Store } from "jotai/vanilla/store";
import { Provider as JotaiProvider } from "jotai/react";
window.ResizeObserver = ResizeObserver;

if (typeof Promise.withResolvers !== "function") {
  Promise.withResolvers = function <T>(): {
    promise: Promise<T>;
    resolve: (value: T | PromiseLike<T>) => void;
    reject: (reason?: any) => void;
  } {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: any) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// eslint-disable-next-line react-refresh/only-export-components
const Root: React.FC<{ jotaiStore: JotaiStore }> = ({ jotaiStore }) => (
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  <Provider store={store}>
    {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
    <PersistGate loading={null} persistor={persistor as Persistor}>
      <JotaiProvider store={jotaiStore}>
        <Providers>
          <NuqsAdapter
            defaultOptions={{
              clearOnDefault: true,
              shallow: false,
            }}
          >
            <App />
          </NuqsAdapter>
        </Providers>
      </JotaiProvider>
    </PersistGate>
  </Provider>
);

// const apm = initApm({
//   serverUrl: process.env.REACT_APP_ELASTIC_APM_CLIENT_SERVER_URL,
//   serviceName: process.env.REACT_APP_ELASTIC_APM_SERVICE_NAME_CLIENT,
//   // active: process.env.NODE_ENV === "production",
// });

const initToken = () => {
  const envToken = process.env.REACT_APP_TOKEN;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const { auth, organization } = store.getState();
  const reduxToken = organization.token || auth.token;
  if (envToken) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    setToken.forAll(store.dispatch, envToken);
  } else if (reduxToken) {
    setToken.forApi(reduxToken);
  }
};

const userBrowserFingerPrintCookie = browserCookie.getCookie(
  commonConstants.cookieKeys.fingerPrint(String(process.env?.REACT_APP_COOKIE_PREFIX || "test")),
);
if (userBrowserFingerPrintCookie) {
  apiConfigs.updateConfig({
    customHeaders: {
      "x-payping-user-hash": userBrowserFingerPrintCookie,
    },
  });
}
userBrowserFingerPrint().then((res) => {
  if (res.fingerprints.persistentFp !== userBrowserFingerPrintCookie) {
    browserCookie.setCookie(
      commonConstants.cookieKeys.fingerPrint(String(process.env?.REACT_APP_COOKIE_PREFIX || "test")),
      res.fingerprints.persistentFp,
      {
        expires: 365,
        path: "/",
      },
    );
    apiConfigs.updateConfig({
      customHeaders: {
        "x-payping-user-hash": res.fingerprints.persistentFp,
      },
    });
  }

  // apm.addLabels({ userFingerPrintHash: res.fingerprints.persistentFp });
});

export let jotaiStore: Store;

const renderAppOnRehydrate = async (): Promise<void> => {
  initToken();
  await initI18next(localeNameSpaces.Wrapper, localeResources);
  jotaiStore = bootstrapJotai();

  const root = createRoot(document.getElementById("root") as HTMLDivElement);
  const ProfiledApp = Sentry.withProfiler(() => <Root jotaiStore={jotaiStore} />);
  root.render(
    <AppErrorBoundary>
      <ProfiledApp />
    </AppErrorBoundary>,
  );
};
export const { store, persistor } = configureStore<AppStoreStates>({
  ...reduxConfig,
  onRehydrated: renderAppOnRehydrate,
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
// serviceWorkerRegistration.unregister();
serviceWorkerRegistration.register();
// serviceWorkerRegistration.register({
//   onUpdate: ({ update }) => {
//     Modal.confirm({
//       cancelText: "الان نه",
//       // eslint-disable-next-line no-useless-concat
//       content: "نسخه جدیدتری در دسترس است" + "\n" + "مایلید بروزرسانی انجام شود؟",
//       okText: "بله",
//       onOk: () => {
//         update();
//         setTimeout(() => {
//           window.location.reload();
//         }, 500);
//       },
//       title: "بروزرسانی",
//     });
//   },
// });
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals

reportWebVitals(sendToAnalytics);
